<template>
	<div>
		<div
			:id="id"
			class="relative w-full h-full"
			:style="staticContainerStyles"
		>
			<!-- Image layer - Load immediately with pre-calculated positioning -->
			<div
				class="absolute h-full w-full"
				:style="staticImageStyles"
			>
				<slot name="image">
					<!-- Use native img for high priority images -->
					<img
						v-if="fetchpriority === 'high'"
						:src="optimizedImageUrl"
						:alt="image.alt || 'Image'"
						:width="reducedImageWidth"
						:height="reducedImageHeight"
						fetchpriority="high"
						loading="eager"
						class="h-full w-full"
						:class="{ 'rounded-full': rounded }"
						style="width: 100%; height: 100%; object-fit: fill;"
						decoding="async"
					>
					<!-- Use NuxtImg for other images -->
					<NuxtImg
						v-else
						:provider="isLocalImage ? 'ipx' : provider"
						:src="image.url"
						:alt="image.alt || 'Image'"
						:width="reducedImageWidth"
						:height="reducedImageHeight"
						:sizes="optimizedSizes"
						:densities="image.densities || 'x1'"
						format="webp"
						quality="85"
						:loading="lazy ? 'lazy' : 'eager'"
						class="h-full w-full"
						:class="{ 'rounded-full': rounded }"
						style="width: 100%; height: 100%;"
						decoding="async"
						:preload="preload"
					/>
				</slot>
			</div>

			<!-- SVG animations - Only render when not deferred -->
			<template v-if="!deferAnimations">
				<!-- SVG underlay -->
				<svg
					width="100%"
					height="100%"
					:viewBox="svgViewBox"
					xmlns="http://www.w3.org/2000/svg"
					class="absolute inset-0 overflow-visible pointer-events-none -z-1"
					preserveAspectRatio="xMidYMid meet"
				>
					<!-- decorative circles -->
					<template v-if="props.circles?.length">
						<Motion
							v-for="(c, i) in circles"
							:key="`circle-${i}`"
							as-child
							:initial="{ scale: 0.8, opacity: 0 }"
							:while-in-view="{ scale: 1, opacity: 1 }"
							:transition="{ type: 'spring', bounce: 0.5, duration: 1.3, delay: 0.5 + i * 0.1 }"
							:in-view-options="{ once: true }"
						>
							<circle
								:cx="c.cx"
								:cy="c.cy"
								:r="c.r"
								:fill="c.fill"
							/>
						</Motion>
					</template>

					<!-- arcs -->
					<template v-if="arcs?.length">
						<Motion
							v-for="(arc, i) in arcs"
							:key="`arc-${i}`"
							as-child
							:initial="{ x: -20, opacity: 0 }"
							:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
							:transition="{ duration: 0.5, delay: 0.5 + i * 0.1 }"
							:in-view-options="{ once: true }"
						>
							<path
								:d="arc.d"
								:stroke="arc.stroke"
								stroke-miterlimit="10"
								fill="none"
							/>
						</Motion>
					</template>
				</svg>

				<!-- SVG overlay -->
				<svg
					width="100%"
					height="100%"
					:viewBox="svgViewBox"
					xmlns="http://www.w3.org/2000/svg"
					class="absolute inset-0 overflow-visible pointer-events-none z-10"
					preserveAspectRatio="xMidYMid meet"
				>
					<!-- decorative circles with icons -->
					<g
						v-for="(icon, i) in icons"
						:key="`icon-group-${i}`"
					>
						<Motion
							as-child
							:initial="{ scale: 0.8, opacity: 0, rotate: i % 2 === 0 ? -5 : 5 }"
							:while-in-view="{ scale: 1, opacity: 1, rotate: 0 }"
							:transition="{ type: 'spring', bounce: 0.5, duration: 1.6, delay: 0.7 + i * 0.1 }"
							:in-view-options="{ once: true }"
						>
							<foreignObject
								:x="icon.cx - icon.r"
								:y="icon.cy - icon.r"
								:width="icon.r * 2"
								:height="icon.r * 2"
							>
								<div
									class="w-full h-full p-2 rounded-full flex items-center justify-center bg-default"
									:class="icon.borderClass || 'border-2 border-secondary'"
								>
									<UIcon
										:name="icon.iconName"
										:class="[
											icon.iconClass || 'text-primary',
											'w-full h-full'
										]"
									/>
								</div>
							</foreignObject>
						</Motion>
					</g>
				</svg>
			</template>

			<!-- Static SVG elements when animations are deferred (No Motion) -->
			<template v-else>
				<svg
					width="100%"
					height="100%"
					:viewBox="svgViewBox"
					xmlns="http://www.w3.org/2000/svg"
					class="absolute inset-0 overflow-visible pointer-events-none -z-1"
					preserveAspectRatio="xMidYMid meet"
				>
					<template v-if="props.circles?.length">
						<circle
							v-for="(c, i) in circles"
							:key="`static-circle-${i}`"
							:cx="c.cx"
							:cy="c.cy"
							:r="c.r"
							:fill="c.fill"
							opacity="1"
						/>
					</template>
					<template v-if="props.lines?.length">
						<path
							v-for="(arc, i) in staticArcs"
							:key="`static-arc-${i}`"
							:d="arc.d"
							:stroke="arc.stroke"
							stroke-miterlimit="10"
							fill="none"
							opacity="1"
						/>
					</template>
				</svg>

				<svg
					v-if="icons?.length"
					width="100%"
					height="100%"
					:viewBox="svgViewBox"
					xmlns="http://www.w3.org/2000/svg"
					class="absolute inset-0 overflow-visible pointer-events-none z-10"
					preserveAspectRatio="xMidYMid meet"
				>
					<foreignObject
						v-for="(icon, i) in icons"
						:key="`static-icon-${i}`"
						:x="icon.cx - icon.r"
						:y="icon.cy - icon.r"
						:width="icon.r * 2"
						:height="icon.r * 2"
					>
						<div
							class="w-full h-full p-2 rounded-full flex items-center justify-center bg-default"
							:class="icon.borderClass || 'border-2 border-secondary'"
						>
							<UIcon
								:name="icon.iconName"
								:class="[
									icon.iconClass || 'text-primary',
									'w-full h-full'
								]"
							/>
						</div>
					</foreignObject>
				</svg>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import type {
	Arc,
	Circle,
	Icon,
	Image,
	Line,
	Viewbox
} from '@/types/components/svg-image'
import { Motion } from 'motion-v'

const DEFAULT_CONFIG = {
	width: 405,
	height: 405,
	offset: 0,
	rotation: 0,
	length: 180,
	direction: 'clockwise',
	gap: 30,
	stroke: '#FF5733'
} as const

const IMAGE_SCALE_FACTOR = 0.8

const props = defineProps({
	id: {
		type: String,
		required: true
	},
	provider: {
		type: String,
		default: 'ipx'
	},
	fetchpriority: {
		type: String,
		default: 'auto'
	},
	lazy: {
		type: Boolean,
		default: false
	},
	rounded: {
		type: Boolean,
		default: false
	},
	deferAnimations: {
		type: Boolean,
		default: false
	},
	viewbox: {
		type: Object as PropType<Viewbox>,
		default: () => ({
			width: 405,
			height: 405
		})
	},
	image: {
		type: Object as PropType<Image>,
		default: () => ({
			url: '/home_content_aside_1.webp',
			alt: 'Maverix Fiber-fast internet',
			offsetX: 0,
			offsetY: 0,
			height: 405,
			width: 405
		})
	},
	sizes: {
		type: String,
		default: '200px sm:360px lg:320px xl:417px'
	},
	preload: {
		type: Boolean,
		default: false
	},
	lines: {
		type: Array as PropType<Line[]>,
		default: () => ([])
	},
	circles: {
		type: Array as PropType<Circle[]>,
		default: () => ([])
	},
	icons: {
		type: Array as PropType<Icon[]>,
		default: () => ([])
	}
})

// ------------------------------
// ------ Pre-calculations ------ // avoid reactive overhead
// ------------------------------
const viewboxWidth = props.viewbox?.width || DEFAULT_CONFIG.width
const viewboxHeight = props.viewbox?.height || DEFAULT_CONFIG.height
const imageOffsetX = props.image?.offsetX || DEFAULT_CONFIG.offset
const imageOffsetY = props.image?.offsetY || DEFAULT_CONFIG.offset
const imageWidth = props.image?.width || viewboxWidth
const imageHeight = props.image?.height || viewboxHeight

const staticContainerStyles = {
	aspectRatio: `${viewboxWidth} / ${viewboxHeight}`,
	maxWidth: `${viewboxWidth}px`,
	maxHeight: `${viewboxHeight}px`
}

const staticImageStyles = {
	left: `${(imageOffsetX / viewboxWidth) * 100}%`,
	top: `${(imageOffsetY / viewboxHeight) * 100}%`,
	width: `${(imageWidth / viewboxWidth) * 100}%`,
	height: `${(imageHeight / viewboxHeight) * 100}%`
}

const svgViewBox = `0 0 ${viewboxWidth} ${viewboxHeight}`

const buildArcs = () => {
	if (!props.lines?.length) return []

	const centerX = viewboxWidth / 2
	const centerY = viewboxHeight / 2

	return props.lines.map((line: Line) => {
		const {
			rotation = DEFAULT_CONFIG.rotation,
			length = DEFAULT_CONFIG.length,
			direction = DEFAULT_CONFIG.direction,
			gap = DEFAULT_CONFIG.gap,
			stroke = DEFAULT_CONFIG.stroke
		} = line

		const r = Math.min(viewboxHeight, viewboxWidth) / 2 + gap
		const startRad = (rotation * Math.PI) / 180
		const sweepRad = (length * Math.PI) / 180
		const endRad = direction === 'clockwise'
			? startRad + sweepRad
			: startRad - sweepRad

		const x1 = centerX + r * Math.sin(startRad)
		const y1 = centerY - r * Math.cos(startRad)
		const x2 = centerX + r * Math.sin(endRad)
		const y2 = centerY - r * Math.cos(endRad)

		const largeArcFlag = length > 180 ? 1 : 0
		const sweepFlag = direction === 'clockwise' ? 1 : 0

		return {
			d: `M${x1},${y1} A${r},${r} 0 ${largeArcFlag},${sweepFlag} ${x2},${y2}`,
			stroke: stroke
		}
	})
}

const staticArcs = buildArcs()

// ---------------------------------
// ------ Computed Properties ------
// ---------------------------------
const arcs = computed<Arc[]>(() => props.deferAnimations ? [] : staticArcs)

// Check if the image is local
const isLocalImage = computed(() => {
	const url = props.image?.url
	return url ? url.startsWith('/') && !url.includes('storyblok.com') : false
})

// Generate optimized Storyblok URL
const optimizedImageUrl = computed(() => {
	if (!props.image?.url) return ''

	if (props.image.url.includes('storyblok.com')) {
		const baseUrl = props.image.url
		return `${baseUrl}/m/${reducedImageWidth.value}x${reducedImageHeight.value}/filters:format(webp):quality(85)`
	}

	return props.image.url
})

// --------------------------------------------
// ------ Manual responsive image sizing ------
// --------------------------------------------
const getSizes = () => {
	const DEFAULT_SIZE = imageWidth || DEFAULT_CONFIG.width
	const sizeMatches = props.sizes.match(/(?:(?:sm|md|lg|xl|2xl):)?(\d+)px/g)
	if (!sizeMatches) return [DEFAULT_SIZE]

	return sizeMatches.map(match => {
		const size = match.match(/(\d+)px/)
		return size && size[1] ? parseInt(size[1]) : DEFAULT_SIZE
	})
}

const parsedSizes = getSizes()

const getbaseImageWidth = () => {
	const maxContainerWidth = Math.max(...parsedSizes)
	const imageWidthRatio = imageWidth / viewboxWidth
	return Math.round(maxContainerWidth * imageWidthRatio) || DEFAULT_CONFIG.width
}

const baseImageWidth = getbaseImageWidth()
const baseImageHeight = Math.round(baseImageWidth / (imageWidth / imageHeight))
const reducedImageWidth = computed(() => Math.round(baseImageWidth * IMAGE_SCALE_FACTOR))
const reducedImageHeight = computed(() => Math.round(baseImageHeight * IMAGE_SCALE_FACTOR))

const getOptimizedSizes = () => {
	const imageWidthRatio = imageWidth / viewboxWidth
	const optimizedBreakpoints = parsedSizes?.map(size => {
		const actualImageSize = Math.round(size * imageWidthRatio)
		return Math.round(actualImageSize * IMAGE_SCALE_FACTOR)
	})

	const sizeStrings: string[] = []
	const breakpoints = ['', 'sm:', 'lg:', 'xl:']

	optimizedBreakpoints.forEach((size, index) => {
		if (index < breakpoints.length && breakpoints[index] !== undefined) {
			sizeStrings.push(`${breakpoints[index]}${size}px`)
		}
	})

	return sizeStrings.join(' ')
}

const optimizedSizes = computed(() => {
	return getOptimizedSizes()
})
</script>
